import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { useCurrentUser } from '../../../../../../hooks/useCurrentUser/useCurrentUser';
import { UserENT } from '../../../../../../types/models/user/user.types';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { ProfileCompanyFormProps, ProfileCompanyFormSchema } from './ProfileCompanyForm.types';
import { getProfileCompanySchema } from './ProfileCompanyForm.schema';
import {
  CREATE_PENDING_COMPANY_QUERY_KEY,
  createPendingCompanyQuery,
} from './ProfileCompanyForm.query';
import { profileCompanyData, profileCompanyDisabledData } from './ProfileCompanyForm.data';
import { useCompany } from '../../../../../../hooks/fetchers/useCompany/useCompany';
import { usePlansENT } from '../../../../../../hooks/fetchers/usePlansENT/usePlansENT';
import { CompanyLegalStatutes } from '../../../../../../types/models/company/company.types';
import SearchInput from '../../../../../commons/SearchInput/SearchInput';
import Input from '../../../../../commons/Input/Input';
import Select from '../../../../../commons/Select/Select';
import { useAddressAutocomplete } from '../../../../../../hooks/fetchers/useAddressAutocomplete/useAddressAutocomplete';
import { AddressSuggestion } from '../../../../../../types/models/address/address.types';
import departements from '../../../../../../assets/json/departements-region.json';
import Button from '../../../../../designSystem/Button/Button';
import { ButtonColor, ButtonSize } from '../../../../../designSystem/Button/Button.types';
import Loading from '../../../../../commons/Loading/Loading';
import { useNavigate } from 'react-router-dom';
import { BaseResponse } from '../../../../../../types/api/api.types';
import moment from 'moment';
import { useCurrentLogo } from '../../../../../../hooks/fetchers/useCurrentLogo/useCurrentLogo';
import PlaceholderImage from "../../../../../../assets/images/placeholder2.jpg";
import { REMOVE_LOGO_ENT_QUERY_KEY, UPLOAD_LOGO_ENT_QUERY_KEY, queryRemoveLogo, queryUploadLogo } from '../../../../authentication/RegisterSteps/RegisterCompanyStep/RegisterCompanyStepForm/RegisterCompanyStepForm.query';
import { useDropzone } from 'react-dropzone';

export default ({ disabled, pendingCompany }: ProfileCompanyFormProps) => {
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const { acceptedFiles, open } = useDropzone({
    multiple: false,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg'],
      // 'application/pdf': ['.pdf'],
    },
  });

  const currentUser = useCurrentUser();

  // const [isModalOpen, setModalOpen] = useState(false)

  const [searchInputValue, setSearchInputValue] = useState<string>('');
  
  const [displayedImage, setDisplayedImage] = useState<string>();

  const { data: addressSuggestions } = useAddressAutocomplete(searchInputValue);

  const { data: plansENT } = usePlansENT();

  const { data: userLogo } = useCurrentLogo();

  useEffect(() => {
    if (userLogo) {
      // @ts-ignore
      setDisplayedImage(URL.createObjectURL(userLogo))
      console.log('userLogo', userLogo)
    }
  }, [userLogo])

  // @ts-ignore
  const { data: company, isInitialLoading } = useCompany();

  const formProps = useForm<ProfileCompanyFormSchema>({
    resolver: yupResolver(getProfileCompanySchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const { handleSubmit, control, setValue, formState } = formProps;

  const { isValid, errors } = formState;

  useEffect(() => {
    if (company) {
      setValue('name', (pendingCompany || company).name, { shouldValidate: true });
      setValue('address', (pendingCompany || company).address, { shouldValidate: true });
      setValue('city', (pendingCompany || company).city, { shouldValidate: true });
      setValue('postalCode', (pendingCompany || company).postalCode, { shouldValidate: true });
      setValue('department', (pendingCompany || company).department, { shouldValidate: true });
      setValue('region', (pendingCompany || company).region, { shouldValidate: true });
      setValue('siret', (pendingCompany || company).siret, { shouldValidate: true });
      // setValue('firstName_owner', (pendingCompany || company).firstName_owner, {
      //   shouldValidate: true,
      // });
      // setValue('lastName_owner', (pendingCompany || company).lastName_owner, {
      //   shouldValidate: true,
      // });
      // setValue('function_owner', (pendingCompany || company).function_owner, {
      //   shouldValidate: true,
      // });
      if ((pendingCompany || company).email_owner) {
        setValue('email_owner', (pendingCompany || company).email_owner, { shouldValidate: true });
      }
      if ((pendingCompany || company).phone_owner) {
        setValue('phone_owner', (pendingCompany || company).phone_owner, { shouldValidate: true });
      }
      if ((pendingCompany || company).latitude && (pendingCompany || company).longitude) {
        setValue('latitude', (pendingCompany || company).latitude)
        setValue('longitude', (pendingCompany || company).longitude)
      }
    }
  }, [company]);

  const { mutate, isLoading } = useMutation<Awaited<BaseResponse>, Error, ProfileCompanyFormSchema>(
    (data) => createPendingCompanyQuery(data),
    {
      mutationKey: CREATE_PENDING_COMPANY_QUERY_KEY,
      onMutate: () => queryClient.cancelMutations(),
      onSuccess: () => {
        navigate(0);
      },
      onError: (error) => {
        alert(error.message);
      },
    }
  );

  const { mutate: uploadLogo } = useMutation<Awaited<BaseResponse>, Error, FormData>(
    (data) => queryUploadLogo(data),
    {
      mutationKey: UPLOAD_LOGO_ENT_QUERY_KEY,
      onMutate: () => queryClient.cancelMutations(),
      onSuccess: async ({ message }) => {
        setDisplayedImage(URL.createObjectURL(acceptedFiles[0]))
        alert(message)
      },
      onError: async (error) => {
        alert(error.message);
      },
    }
  );

  const { mutate: removeLogo } = useMutation<Awaited<BaseResponse>, Error>(
    () => queryRemoveLogo(),
    {
      mutationKey: REMOVE_LOGO_ENT_QUERY_KEY,
      onMutate: () => queryClient.cancelMutations(),
      onSuccess: async ({ message }) => {
        setDisplayedImage(undefined)
        alert(message)
      },
      onError: async (error) => {
        alert(error.message);
      },
    }
  );

  useEffect(() => {
    if (acceptedFiles.length > 0) {
      const formData = new FormData();
      for (const file of acceptedFiles) {
        formData.append('image', file);
      }
      uploadLogo(formData);
    }
  }, [acceptedFiles])

  const onAddressChange = (
    e: ChangeEvent<HTMLInputElement>,
    onChange: (e: ChangeEvent<HTMLInputElement>) => void
  ) => {
    const value = e.target.value;
    onChange(e);
    setSearchInputValue(value);
  };

  const handleSelectedSuggestion = (suggestion: AddressSuggestion) => {
    setSearchInputValue('');
    setValue('address', suggestion.address);
    setValue('city', suggestion.city);
    setValue('postalCode', suggestion.postalCode);
    setValue('department', suggestion.department);
    setValue(
      'region',
      departements.find((res) => res.num_dep.toString() == suggestion.department_code)
        ?.region_name || ''
    );
    if (suggestion.coordinates) {
      setValue('latitude', suggestion.coordinates.latitude)
      setValue('longitude', suggestion.coordinates.longitude)
    }
  };

  const onSubmit = handleSubmit((data) => mutate(data));

  const updatedFormData = useMemo(
    () =>
      (disabled ? profileCompanyDisabledData : profileCompanyData).map((row) => {
        return row.map((item) => {
          switch (item.name) {
            case 'plan':
              return {
                ...item,
                disabled: true,
                value: {
                  value: (currentUser as UserENT).id_plan,
                  label: plansENT?.find(({ id }) => id === (currentUser as UserENT).id_plan)?.label,
                },
                options: plansENT?.map((plan) => ({
                  value: plan.id,
                  label: plan.label,
                })),
              };
            case 'legalStatutes':
              return {
                ...item,
                disabled: true,
                value: {
                  value: company?.legalStatutes,
                  label:
                    CompanyLegalStatutes[
                      company?.legalStatutes as keyof typeof CompanyLegalStatutes
                    ],
                },
              };
            default:
              return item;
          }
        });
      }),
    [plansENT, company, currentUser]
  );

  if (isInitialLoading) return <Loading />;

  return (
    <form className="form">
    <div className="form__row" 
      style={{ 
        marginBottom: 'var(--spacing-sToM)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
      <div className='register__company-logo-box'>
        <img 
          className='register__company-logo-img' 
          src={displayedImage || PlaceholderImage} 
        />
      </div>
      {displayedImage &&
        <button 
          className='register__company-logo-button register__company-logo-button-remove'
          onClick={(e) => {
            e.preventDefault()
            removeLogo()
          }}
        >
          Supprimer
        </button>
      }
      <button 
        className='register__company-logo-button'
        onClick={(e) => {
          e.preventDefault()
          open()
        }}
      >
        Importer un logo
      </button>
    </div>
      {updatedFormData.map((row, rowIndex) => (
        <div key={rowIndex} className="form__row">
          {row.map(
            ({ name, placeholder, label, type, options, defaultValue, ...props }, inputIndex) => (
              <Controller
                key={inputIndex}
                name={name}
                render={({ field: { onChange, value, ...rest } }) =>
                  name === 'address' ? (
                    <SearchInput
                      suggestions={addressSuggestions}
                      placeholder={placeholder || ''}
                      type={type}
                      onChange={(e) => onAddressChange(e, onChange)}
                      handleSelectedSuggestion={handleSelectedSuggestion}
                      error={errors[name]?.message as string}
                      // @ts-ignore
                      value={value}
                      {...{ label, name }}
                      {...props}
                    />
                  ) : type !== 'select' ? (
                    <Input
                      placeholder={placeholder || ''}
                      type={type}
                      disabled={Boolean(
                        ['department', 'region', 'city', 'postalCode'].includes(name)
                      )}
                      error={errors[name]?.message as string}
                      // @ts-ignore
                      value={value}
                      {...{ onChange, label, name }}
                      {...props}
                    />
                  ) : (
                    <Select
                      placeholder={placeholder || ''}
                      error={errors[name]?.message as string}
                      {...{ label, options, defaultValue, ...rest }}
                      {...props}
                      // @ts-ignore
                      onChange={({ value }: SelectOption) => {
                        onChange(value);
                      }}
                    />
                  )
                }
                {...{ control }}
              />
            )
          )}
        </div>
      ))}
      {/* <span style={{ marginBottom: 'var(--spacing-sToM)' }}>Pour toute modification des informations de votre entreprise, veuillez nous contacter à <a href='mailto:contact@publiconsult.fr'>contact@publiconsult.fr</a></span> */}
      {disabled && pendingCompany && (
        <span style={{ marginBottom: 'var(--spacing-sToM)' }}>
          Vos modifications effectuées le {moment(pendingCompany.created_at).format('DD/MM/YYYY')}{' '}
          sont en attente de validation.
        </span>
      )}
      <Button
        // style={{ display: 'none' }}
        onClick={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        size={ButtonSize.Large}
        disabled={!isValid || disabled}
        color={ButtonColor.Secondary}
        isLoading={isLoading}>
        Valider
      </Button>
    </form>
  );
};
